import React from 'react';
import {action, computed, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {UVTemplateLine, UVTemplateOptions} from '../../i';
import {Font, FONT_FAMILY_MAP, loadEPFont} from '../engraved-plates/ep-extended/engraved-plate-extended.store';
import {UV_FONT_OPTIONS, UV_FONT_SWATCHES, uvFontLabel, uvFontSampleName} from './uv-fonts';
import {defaultUVOptions, isStaticUVLine, uvLineMaxLength, uvLineTitle} from './uv-template';
import './uv-print.scss';

// Shopper-selectable UV text colors. The hex value is written to each line's
// color and rides the uv_lines JSON through render + add-to-cart + imagegen.
const UV_TEXT_COLORS: {label: string; value: string}[] = [
    {label: 'White', value: '#FFFFFF'},
    {label: 'Gold', value: '#C5AA68'},
    {label: 'Silver', value: '#969DAE'},
    {label: 'Black', value: '#000000'},
];

const resolveFontFamily = (raw: string): string => {
    if (!raw) return '';
    const asNum = parseInt(raw, 10);

    if (!isNaN(asNum) && FONT_FAMILY_MAP[asNum as Font]) {
        return FONT_FAMILY_MAP[asNum as Font];
    }

    return raw;
};

@observer
export default class UVPrint extends React.Component {
    // 'none' | 'personalize' — the PERSONALIZE toggle. Starts at None; nothing
    // else renders until the shopper opts into Personalize.
    @observable mode: 'none' | 'personalize' = 'none';

    constructor(props) {
        super(props);
        makeObservable(this);

        // A revisited product (restored state) with personalization already on
        // opens in Personalize mode so the shopper sees their entries.
        if (s.ps.uv_print_active || this.hasAnyText) {
            this.mode = 'personalize';
        }
    }

    componentDidMount() {
        this.lines.forEach(({ln}) => {
            const fontId = parseInt(ln.font || '', 10);

            if (!isNaN(fontId) && FONT_FAMILY_MAP[fontId as Font]) {
                loadEPFont(fontId as Font);
            }
        });
    }

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    // Only lines with valid template geometry are editable/rendered (mirrors
    // build_uv_lines in web-only-render.ts). `index` is the position in the
    // s.prod.uv_lines array so edits write back to the correct element.
    @computed get lines(): {ln: UVTemplateLine; index: number}[] {
        const arr = s.prod.uv_lines || [];
        const out: {ln: UVTemplateLine; index: number}[] = [];

        for (let i = 0; i < arr.length; i++) {
            const ln = arr[i];

            if (!ln) continue;
            if (typeof ln.x !== 'number' || typeof ln.y !== 'number' || !ln.pt || !ln.max_width || !ln.max_height) continue;

            out.push({ln, index: i});
        }

        return out;
    }

    // The lines the shopper types into: a static line is fixed template text.
    @computed get editableLines(): {ln: UVTemplateLine; index: number}[] {
        return this.lines.filter(({ln}) => !isStaticUVLine(ln));
    }

    @computed get options(): UVTemplateOptions {
        return s.prod.uv_options || defaultUVOptions();
    }

    @computed get hasAnyText(): boolean {
        const arr = s.prod.uv_lines || [];

        return arr.some(ln => !!ln && !isStaticUVLine(ln) && (ln.value || '').length > 0);
    }

    // The design (font) selection is global: every line carries the same font
    // id, so the first line's font is the selected design.
    @computed get selectedFontId(): Font | null {
        const first = this.lines[0];

        if (!first) return null;

        const asNum = parseInt(first.ln.font || '', 10);

        return !isNaN(asNum) && FONT_FAMILY_MAP[asNum as Font] ? (asNum as Font) : null;
    }

    // Like the design, the text color is global across lines. A template color
    // outside the shopper palette (legacy/custom) just shows no selection.
    @computed get selectedColor(): {label: string; value: string} | null {
        const first = this.lines[0];

        if (!first) return null;

        const raw = ((first.ln.color as string) || '#FFFFFF').toLowerCase();

        return UV_TEXT_COLORS.find(c => c.value.toLowerCase() === raw) || null;
    }

    @action
    setMode = (mode: 'none' | 'personalize') => {
        this.mode = mode;

        if (mode === 'none') {
            // None = no personalization on the frame. Entered text is kept so
            // switching back to Personalize restores it.
            if (s.ps.uv_print_active) {
                s.ps.uv_print_active = false;
                s.ui.doRender = true;
                s.ui.update_pricing = true;
                s.pulse();
            }

            return;
        }

        // Re-activate immediately when the shopper already typed text earlier;
        // otherwise activation happens on the first keystroke (onTextChange).
        if (!s.ps.uv_print_active && this.hasAnyText) {
            s.ps.uv_print_active = true;
            s.ui.doRender = true;
            s.ui.update_pricing = true;
            s.pulse();
        }
    };

    @action
    onTextChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const ln = s.prod.uv_lines[index];

        if (ln) ln.value = e.target.value;

        // Auto-add the personalization to the frame as soon as the user starts
        // typing, so they don't have to click anything else first.
        if (!s.ps.uv_print_active && this.hasAnyText) {
            s.ps.uv_print_active = true;
            s.ui.doRender = true;
            s.ui.update_pricing = true;
            s.pulse();

            return;
        }

        if (s.ps.uv_print_active) {
            s.ui.doRender = true;
            s.pulse();
        }
    };

    @action
    selectDesign = (fontId: Font) => {
        // One design drives the whole personalization: apply to every line.
        this.lines.forEach(({index}) => {
            const ln = s.prod.uv_lines[index];

            if (ln) ln.font = String(fontId);
        });

        loadEPFont(fontId);

        if (s.ps.uv_print_active) {
            s.ui.doRender = true;
            s.pulse();
        }
    };

    @action
    selectColor = (value: string) => {
        this.lines.forEach(({index}) => {
            const ln = s.prod.uv_lines[index];

            if (ln) ln.color = value;
        });

        if (s.ps.uv_print_active) {
            s.ui.doRender = true;
            s.pulse();
        }
    };

    renderDesignSwatch(fontId: Font) {
        const selected = this.selectedFontId === fontId;
        const label = uvFontLabel(fontId);
        const swatch = UV_FONT_SWATCHES[fontId];

        // Live-render fallback for fonts without a pre-generated swatch image:
        // a white 75x75 square with a sample name in the actual font.
        if (!swatch) {
            loadEPFont(fontId);
        }

        return (
            <button
                key={fontId}
                type="button"
                className={`uv-design-swatch${selected ? ' selected' : ''}`}
                aria-label={`Select ${label} design`}
                aria-pressed={selected}
                title={label}
                onClick={() => this.selectDesign(fontId)}
            >
                {swatch ? (
                    <img
                        src={swatch}
                        alt={label}
                        width={75}
                        height={75}
                    />
                ) : (
                    <span
                        className="uv-design-swatch-live"
                        style={{fontFamily: FONT_FAMILY_MAP[fontId]}}
                    >
                        {uvFontSampleName(fontId)}
                    </span>
                )}
            </button>
        );
    }

    renderLineEditor(ln: UVTemplateLine, index: number) {
        const text = (ln.value as string) || '';
        const max = uvLineMaxLength(ln);
        const required = this.options.required && ln.optional !== true;
        const previewFontFamily = resolveFontFamily((ln.font as string) || '') || 'Arial, sans-serif';
        const label = uvLineTitle(ln);
        const inputId = `uv-print-line-${index}`;

        return (
            <div
                key={index}
                className="uv-print-line"
            >
                <div className="uv-print-line-head">
                    <label
                        className="uv-print-line-label"
                        htmlFor={inputId}
                    >
                        {this.editableLines.length > 1 || this.options.required ? label : `Enter Text (up to ${max} characters)`}
                        {required && (
                            <span
                                className="uv-print-required-mark"
                                aria-hidden="true"
                            >
                                {' '}
                                *
                            </span>
                        )}
                    </label>
                </div>
                <input
                    id={inputId}
                    type="text"
                    className="uv-print-input"
                    maxLength={max}
                    aria-label={`${label} text`}
                    aria-required={required || undefined}
                    placeholder={ln.ph || 'Enter Text'}
                    autoComplete="off"
                    value={text}
                    onChange={this.onTextChange(index)}
                    style={{fontFamily: previewFontFamily}}
                />
                <div className="uv-print-char-count">
                    {text.length}/{max}
                </div>
            </div>
        );
    }

    renderColorSwatch(color: {label: string; value: string}) {
        const selected = this.selectedColor?.value.toLowerCase() === color.value.toLowerCase();

        return (
            <div
                key={color.value}
                className="uv-color-option"
            >
                <button
                    type="button"
                    className={`uv-color-swatch${selected ? ' selected' : ''}`}
                    aria-label={`Select ${color.label} text color`}
                    aria-pressed={selected}
                    title={color.label}
                    onClick={() => this.selectColor(color.value)}
                >
                    <span
                        className="uv-color-swatch-fill"
                        style={{backgroundColor: color.value}}
                    />
                </button>
                <div className="uv-color-label">{color.label}</div>
            </div>
        );
    }

    renderPersonalize() {
        const lines = this.editableLines;
        const opts = this.options;
        const selectedFontId = this.selectedFontId;
        const designLabel = selectedFontId ? uvFontLabel(selectedFontId) : '';
        // Section numbers follow the sections shown (a locked font or colour drops one).
        let step = 0;
        const num = (): string => `${++step}. `;

        return (
            <>
                {!opts.lock_font && (
                    <div className="uv-print-section">
                        <div className="uv-print-section-title">
                            {num()}SELECT A DESIGN:
                            {designLabel && <span className="uv-print-section-value">{designLabel}</span>}
                        </div>
                        <div className="uv-design-grid">{UV_FONT_OPTIONS.map(f => this.renderDesignSwatch(f.value))}</div>
                    </div>
                )}

                <div className="uv-print-section">
                    <div className="uv-print-section-title">{num()}EDIT THE DESIGN</div>
                    {lines.map(({ln, index}) => this.renderLineEditor(ln, index))}
                    {!opts.lock_color && (
                        <>
                            <div className="uv-print-color-title">
                                Select Text Color:
                                {this.selectedColor && <span className="uv-print-section-value">{this.selectedColor.label}</span>}
                            </div>
                            <div className="uv-color-grid">{UV_TEXT_COLORS.map(c => this.renderColorSwatch(c))}</div>
                        </>
                    )}
                </div>

                <div className="uv-print-section">
                    <div className="uv-print-section-title">{num()}DESIGN SUMMARY</div>
                    <div className="uv-print-summary">
                        {!opts.lock_font && (
                            <div>
                                <span className="uv-print-summary-label">Design Template:</span> {designLabel || 'None Selected'}
                            </div>
                        )}
                        {!opts.lock_color && (
                            <div>
                                <span className="uv-print-summary-label">Text Color:</span> {this.selectedColor?.label || 'None Selected'}
                            </div>
                        )}
                        {lines.map(({ln, index}) => (
                            <div key={index}>
                                <span className="uv-print-summary-label">{lines.length > 1 ? uvLineTitle(ln) : 'Text'}:</span>{' '}
                                {((ln.value as string) || '').length > 0 ? <span style={{fontFamily: resolveFontFamily((ln.font as string) || '')}}>{ln.value}</span> : <span className="uv-print-awaiting">Awaiting Entry</span>}
                            </div>
                        ))}
                    </div>
                </div>
            </>
        );
    }

    // A required template (the INFORMS awards): the inputs are the whole box - no
    // opt-in toggle, no design / colour choice (the template locks them), nothing to
    // summarise; the product photo shows the text as it is typed.
    renderRequired() {
        return <div className="uv-print-required">{this.editableLines.map(({ln, index}) => this.renderLineEditor(ln, index))}</div>;
    }

    render() {
        if (!s.ps.is_uv_print || !s.ui.show_cust) {
            return null;
        }

        const lines = this.lines;

        return (
            <div
                className="gray-box uv-print"
                style={{padding: 0}}
            >
                <div>
                    <div
                        style={{
                            paddingLeft: 12,
                            paddingRight: 12,
                            paddingTop: 0,
                            paddingBottom: 0,
                            position: 'relative',
                        }}
                    >
                        <div
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-block toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#uv-print-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'uv-print-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>Personalization</span>
                                {!this.options.required && s.ps.uv_print_active && this.hasAnyText && <span className="uv-print-status"> (Active)</span>}
                            </div>
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-block d-xl-none toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#uv-print-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'uv-print-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        Personalization
                                    </div>
                                    {!this.options.required && s.ps.uv_print_active && this.hasAnyText && <div className="cust-flex2">Active</div>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="uv-print-inner"
                        className={this.canCollapse ? 'collapse' : ''}
                        style={{
                            padding: '0 12px 12px 12px',
                        }}
                    >
                        <div className="uv-print-container">
                            {lines.length === 0 && <div className="uv-print-empty">No UV lines configured for this product.</div>}
                            {lines.length > 0 && this.options.required && this.renderRequired()}
                            {lines.length > 0 && !this.options.required && (
                                <>
                                    <div className="uv-print-section">
                                        <div className="uv-print-section-title">PERSONALIZE:</div>
                                        <div className="uv-print-mode">
                                            <button
                                                type="button"
                                                className={`uv-print-mode-btn${this.mode === 'none' ? ' selected' : ''}`}
                                                aria-pressed={this.mode === 'none'}
                                                onClick={() => this.setMode('none')}
                                            >
                                                None
                                            </button>
                                            <button
                                                type="button"
                                                className={`uv-print-mode-btn${this.mode === 'personalize' ? ' selected' : ''}`}
                                                aria-pressed={this.mode === 'personalize'}
                                                onClick={() => this.setMode('personalize')}
                                            >
                                                Personalize
                                            </button>
                                        </div>
                                    </div>
                                    {this.mode === 'personalize' && this.renderPersonalize()}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
